import React, { useEffect, useState } from 'react';
import { Notice, Link } from '@heartinternet/hi-ui';
import './PageMessages.css';

const MESSAGES_URL = '/manage/messages.cgi';

/** A single per-customer message as returned by messages.cgi. */
export interface PageMessage {
  /** Unique message id — used for the dismiss (message-delete) link. */
  id: string | number;
  /**
   * Message category. `page-*` types are scoped to a single page (shown only
   * when the type equals the current pageId); `LOCKOUT` is not dismissible;
   * everything else is a generic, dismissible message.
   */
  type: string;
  /** Short title, rendered as a heading above the body. */
  subject: string;
  /** Server-provided HTML body (rendered verbatim, matching the legacy page). */
  message: string;
}

/** Shape of the JSON returned by GET /manage/messages.cgi. */
export interface PageMessagesData {
  messages: PageMessage[];
  /** Truthy when the customer has an unfinished basket. */
  has_held_basket?: boolean | number;
  /** Truthy when there are tickets awaiting the customer's response. */
  important_tickets?: boolean | number;
  /** Truthy when there are new/updated tickets. */
  new_tickets?: boolean | number;
}

export interface PageMessagesProps {
  /** Current page identifier, e.g. 'page-manage-hosting'. `page-*` messages show only when their type matches this. */
  pageId: string;
  /**
   * Optional pre-fetched data, used in tests/Storybook to bypass the network.
   * When supplied the component renders this instead of calling fetch().
   */
  data?: PageMessagesData;
}

// Backend booleans may arrive as 0/1 or JSON true — treat both as truthy.
const isTruthy = (v: unknown): boolean => Number(v) === 1 || v === true;

/**
 * Per-customer message banner for the customer portal. Replicates the legacy
 * `load_messages` behaviour from customer/javascript/messages.js: fetches
 * `/manage/messages.cgi` on mount and renders the customer's messages plus the
 * held-basket and ticket notices. Renders nothing until data has arrived, and
 * silently ignores fetch errors (the legacy page shows no error UI).
 */
export const PageMessages: React.FC<PageMessagesProps> = ({ pageId, data }) => {
  const [fetched, setFetched] = useState<PageMessagesData | null>(data ?? null);

  useEffect(() => {
    if (data) return;
    let active = true;
    fetch(MESSAGES_URL, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((json: PageMessagesData) => {
        if (active && json) setFetched(json);
      })
      // Legacy shows no error UI — swallow failures and render nothing.
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [data]);

  if (!fetched) return null;

  const messages = fetched.messages ?? [];

  return (
    <div className="hi-ui-page-messages">
      {messages.map((message) => {
        // page-scoped messages only show on their own page; others always show.
        if (/^page-/.test(message.type) && message.type !== pageId) return null;

        // Legacy load_messages gates DOMAIN-UPSELL behind a hardcoded
        // `DOMAIN_UPSELL = false`, so these never render today. Suppress them
        // to preserve parity (customer/javascript/messages.js).
        if (message.type === 'DOMAIN-UPSELL') return null;

        const isLockout = message.type === 'LOCKOUT';

        return (
          <Notice
            key={String(message.id)}
            variant="info"
            dismissible={!isLockout}
            onDismiss={
              isLockout
                ? undefined
                : () => {
                    // Full navigation, matching the legacy dismiss <a href>.
                    window.location.href = `/manage/index.cgi?message-delete=${encodeURIComponent(
                      String(message.id)
                    )}`;
                  }
            }
          >
            <p className="hi-ui-page-messages__title">{message.subject}</p>
            {/* Server-provided HTML, matching legacy `s.innerHTML = message.message`. */}
            <div dangerouslySetInnerHTML={{ __html: message.message }} />
          </Notice>
        );
      })}

      {isTruthy(fetched.has_held_basket) && (
        <Notice variant="info">
          You have an incomplete purchase in progress. Please{' '}
          <Link href="/manage/pending-purchase.cgi">click here</Link> to complete or cancel it.
        </Notice>
      )}

      {isTruthy(fetched.important_tickets) ? (
        <Notice variant="warning">
          You have tickets which require your response. Please visit the{' '}
          <Link href="/manage/ticket.cgi/">customer service tickets page</Link> for more
          information.
        </Notice>
      ) : (
        isTruthy(fetched.new_tickets) && (
          <Notice variant="info">
            You have new or updated tickets. Please visit the{' '}
            <Link href="/manage/ticket.cgi/">customer service tickets page</Link> for more
            information.
          </Notice>
        )
      )}
    </div>
  );
};
